import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@apollo/client";

import PublicV2Layout from "../layout/PublicV2Layout";
import ReviewsCarouselV2 from "../shared/ReviewsCarouselV2";
import Tracking from "../../components/TrackingComp";
import { SEO_V2 } from "../theme/seoMetaV2";
import { v2Href } from "../publicRoutes";
import { GET_REVIEW_COUNTER } from "../../utils/GraphQL/queries";
import "./homeV2.css";

import GroupsRoundedIcon from "@mui/icons-material/GroupsRounded";
import StarRoundedIcon from "@mui/icons-material/StarRounded";
import WorkspacePremiumRoundedIcon from "@mui/icons-material/WorkspacePremiumRounded";
import AccessTimeRoundedIcon from "@mui/icons-material/AccessTimeRounded";
import ServiceDoorIcon from "../icons/ServiceDoorIcon";
import ServiceSwatchIcon from "../icons/ServiceSwatchIcon";
import ServiceSprayerIcon from "../icons/ServiceSprayerIcon";
import ServiceShieldIcon from "../icons/ServiceShieldIcon";
import "../icons/serviceIcons.css";

// High-quality public (Unsplash license) refinished wood door — bundled so
// the hero never depends on gallery/test data and there's no hotlink/CSP risk.
const heroDoor = require("../../assets/images/hero-door.png");

const TRUST = [
  { icon: <GroupsRoundedIcon />, label: "Family Owned" },
  { icon: <StarRoundedIcon />, label: "5-Star Rated" },
  { icon: <WorkspacePremiumRoundedIcon />, label: "Premium Quality" },
  { icon: <AccessTimeRoundedIcon />, label: "On Time" },
];

const SERVICES = [
  {
    icon: <ServiceDoorIcon />,
    title: "Door Refinishing",
    desc: "We refinish wood doors back to their original beauty — bringing out the natural warmth and character of the wood.",
  },
  {
    icon: <ServiceSwatchIcon />,
    title: "Stain Matching",
    desc: "We match your existing stain as closely as possible, so your refinished door blends right in with your home.",
  },
  {
    icon: <ServiceSprayerIcon />,
    title: "Sealing & Finishing",
    desc: "A smooth, sprayed-on finish that seals the wood and brings out rich, even depth and color.",
  },
  {
    icon: <ServiceShieldIcon />,
    title: "Sun & Weather Protection",
    desc: "Protective topcoats that take the brunt of the heat and weather — with simple upkeep to keep your door looking fresh.",
  },
];

const STATS = [
  { value: "15+", label: "Years of Experience" },
  { value: "8,000+", label: "Doors Refinished" },
  { value: "5-Star", label: "Customer Rating" },
  { value: "100%", label: "Satisfaction Guaranteed" },
];

// Google reviews shown in the hero. Replace with your real rating, count, and
// reviews link (or we can wire a live Google Places fetch — see chat).
const GOOGLE = {
  rating: "5.0",
  count: "200+",
  url: "https://www.google.com/maps/search/?api=1&query=Biggin%27s%20Door%20Refinishing%20San%20Antonio",
};

const HomeV2 = (navProps) => {
  const { preview = false } = navProps;
  // Touch devices have no hover — animate the service icons as each tile
  // scrolls into view. IntersectionObserver is lightweight (no per-frame scroll
  // handler); it toggles `.bdrv2-svc-anim`, which only drives animation under
  // `@media (hover: none)` so desktop stays hover-only.
  useEffect(() => {
    if (typeof IntersectionObserver === "undefined") return undefined;
    const cards = document.querySelectorAll(".bdrv2-home-service-card");
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          e.target.classList.toggle("bdrv2-svc-anim", e.isIntersecting);
        });
      },
      // only fire while the tile is in the middle 40%-60% band of the viewport
      { root: null, rootMargin: "-40% 0px -40% 0px", threshold: 0 }
    );
    cards.forEach((c) => io.observe(c));
    return () => io.disconnect();
  }, []);

  // Review count + rating come from the manually-maintained ReviewCounter
  // (admin Defaults -> Google Reviews). cache-first: fetched once and shared
  // across every page via the normalized cache. Falls back to the static GOOGLE
  // config until a real value is set.
  const { data: reviewData } = useQuery(GET_REVIEW_COUNTER, {
    fetchPolicy: "cache-first",
  });
  const rc = reviewData?.getReviewCounter;
  const hasCount = rc?.reviewCount && rc.reviewCount !== "0";
  const ratingText = rc?.rating ? Number(rc.rating).toFixed(1) : GOOGLE.rating;
  const reviewsText = hasCount
    ? `${rc.reviewCount} Google reviews`
    : `${GOOGLE.count} Google reviews`;
  const reviewsUrl = rc?.url || GOOGLE.url;

  return (
    <PublicV2Layout seo={SEO_V2.home} {...navProps}>
      <Tracking page={"home"} data-nosnippet />
      {/* HERO — full-bleed door photo; text left & vertically centered; info card right */}
      <section className="bdrv2-home-hero">
        <img
          className="bdrv2-home-hero-photo-bg"
          src={heroDoor}
          alt=""
          aria-hidden="true"
          decoding="async"
        />
        <div className="bdrv2-home-hero-scrim" aria-hidden="true" />

        <div className="bdrv2-container bdrv2-home-hero-inner">
          <div className="bdrv2-home-hero-left">
            <p className="bdrv2-eyebrow bdrv2-accent-text">
              Beautiful Doors · Expert Refinishing
            </p>
            <h1 className="bdrv2-home-h1">
              Let our family
              <br />
              serve your family
            </h1>
            <p className="bdrv2-home-lead">
              Biggin&apos;s Door Refinishing is a family-owned company specializing
              in wood door refinishing — we strip, re-stain, and protect your
              doors against the elements.
            </p>
            <div className="bdrv2-home-cta-row">
              <Link to={v2Href(preview, "quote")} className="bdrv2-btn bdrv2-btn--primary">
                Request a Quote
              </Link>
              <Link to={v2Href(preview, "ourDoors")} className="bdrv2-btn bdrv2-btn--ghost">
                See Our Work
              </Link>
            </div>
            <a
              className="bdrv2-home-hero-rating"
              href={reviewsUrl}
              target="_blank"
              rel="noopener noreferrer"
            >
              <span className="bdrv2-home-hero-stars" aria-hidden="true">
                ★★★★★
              </span>
              <span>
                {ratingText} &middot; {reviewsText}
              </span>
            </a>
          </div>

          <aside className="bdrv2-home-hero-card bdrv2-glass">
            <div>
              <h3 className="bdrv2-home-hero-card-title">
                Family-Owned.
                <br />
                Quality Driven.
              </h3>
              <p className="bdrv2-home-hero-card-text">
                For over a decade we&apos;ve refinished doors across San Antonio,
                the Texas Hill Country, and South Texas — one door, one family at
                a time.
              </p>
            </div>
            <div className="bdrv2-home-hero-seal" aria-hidden="true">
              <span className="bdrv2-home-hero-seal-est">EST.</span>
              <span className="bdrv2-home-hero-seal-year">2012</span>
            </div>
          </aside>
        </div>

        <div className="bdrv2-container bdrv2-home-hero-trust">
          {TRUST.map((t) => (
            <div className="bdrv2-home-hero-trust-item" key={t.label}>
              <span className="bdrv2-home-hero-trust-ico bdrv2-accent-text">
                {t.icon}
              </span>
              {t.label}
            </div>
          ))}
        </div>
      </section>

      {/* SERVICES */}
      <section className="bdrv2-section bdrv2-home-services">
        <div className="bdrv2-container">
          <div className="bdrv2-home-section-head">
            <h2>Refinished. Renewed. Protected.</h2>
            <p>Expert care for every wood door.</p>
          </div>
          <div className="bdrv2-home-service-grid">
            {SERVICES.map((s) => (
              <div key={s.title} className="bdrv2-home-service-card bdrv2-glass">
                <div className="bdrv2-home-service-head">
                  <h3>{s.title}</h3>
                  <span className="bdrv2-home-service-ico bdrv2-accent-text">
                    {s.icon}
                  </span>
                </div>
                <p>{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* STATS */}
      <section className="bdrv2-home-stats">
        <div className="bdrv2-container bdrv2-home-stats-row">
          {STATS.map((s) => (
            <div key={s.label} className="bdrv2-home-stat">
              <span className="bdrv2-home-stat-value bdrv2-accent-text">
                {s.value}
              </span>
              <span className="bdrv2-home-stat-label">{s.label}</span>
            </div>
          ))}
        </div>
      </section>

      {/* ABOUT — full-width transparent glass banner over the bg image */}
      <section className="bdrv2-home-about-banner">
        <div className="bdrv2-container bdrv2-home-about">
          <h2>Honoring tradition. Refining excellence.</h2>
          <p>
            At Biggin&apos;s Door Refinishing, we are focused on providing you
            with a quality refinished door while maintaining the highest level of
            customer satisfaction. We will do everything we can to meet your
            expectations.
          </p>
          <p>
            Just as you care about the appearance of your home, we care about the
            service we provide to you. We take pride in our finished product and
            we&apos;re positive you&apos;ll be happy working with us.
          </p>
          <p className="bdrv2-home-motto bdrv2-accent-text">
            Let OUR family serve YOUR family!
          </p>
        </div>
      </section>

      {/* REVIEWS */}
      <section className="bdrv2-section bdrv2-home-reviews">
        <div className="bdrv2-container">
          <div className="bdrv2-home-section-head">
            <h2>What our customers say</h2>
            <p>Real words from real homeowners.</p>
          </div>
          <ReviewsCarouselV2 />
        </div>
      </section>
    </PublicV2Layout>
  );
};

export default HomeV2;
